<template>
  <div class="login-container">
    <div class="left-section">
      <div class="logo">
        <img src="https://peelocar.mojay.pro/assets/img/logoblanc.png" alt="Peelo Logo" />
      </div>
      <a href="https://peelo.chat" class="back-link">
        <span>Retour au site</span>
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M5 12h14M12 5l7 7-7 7"/>
        </svg>
      </a>
      <div class="hero-content">
        <TransitionGroup name="fade-slide">
          <div v-for="(slide, index) in slides" :key="'slide-' + index" v-show="currentSlide === index" class="slide">
            <h2>{{ slide.title }}</h2>
            <p>{{ slide.description }}</p>
          </div>
        </TransitionGroup>
        <div class="dots">
          <span 
            v-for="(slide, index) in slides" 
            :key="'dot-' + index"
            :class="['dot', { active: currentSlideIndex === index }]"
            @click="setSlide(index)"
          ></span>
        </div>
      </div>
    </div>
    <div class="right-section">
      <div class="auth-container">
        <div class="back-button-container">
          <router-link to="/login" class="back-button">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            <span>Retour</span>
          </router-link>
        </div>
        <h1>Inscription</h1>
        <p class="login-text">
          Déjà inscrit ? 
          <router-link to="/login" class="login-link">Se connecter</router-link>
        </p>
        
        <div class="steps-progress">
          <div 
            v-for="step in totalSteps" 
            :key="'step-' + step"
            :class="['step-indicator', { active: currentStep >= step, completed: currentStep > step }]"
          >
            {{ step }}
          </div>
          <div class="progress-line"></div>
        </div>
        
        <div class="form-container">
          <form @submit.prevent="currentStep === totalSteps ? registerUser() : goToNextStep()" class="register-form">
            <!-- Étape 1: Informations personnelles -->
            <div v-if="currentStep === 1" class="step-content">
             

              <div class="form-group">
                <label for="firstname">Prénom</label>
                <input 
                  type="text" 
                  id="firstname" 
                  v-model="formData.firstname" 
                  required
                  placeholder="Entrez votre prénom"
                  :class="{ 'input-error': fieldErrors.firstname }"
                />
                <div v-if="fieldErrors.firstname" class="error-message">{{ fieldErrors.firstname }}</div>
              </div>

              <div class="form-group">
                <label for="lastname">Nom</label>
                <input 
                  type="text" 
                  id="lastname" 
                  v-model="formData.lastname" 
                  required
                  placeholder="Entrez votre nom"
                  :class="{ 'input-error': fieldErrors.lastname }"
                />
                <div v-if="fieldErrors.lastname" class="error-message">{{ fieldErrors.lastname }}</div>
              </div>
            
              <div class="form-group">
                <label for="company">Nom de l'entreprise</label>
                <input 
                  type="text" 
                  id="company" 
                  v-model="formData.company" 
                  required
                  placeholder="Entrez le nom de votre entreprise"
                  :class="{ 'input-error': fieldErrors.company }"
                />
                <div v-if="fieldErrors.company" class="error-message">{{ fieldErrors.company }}</div>
              </div>

              <div class="form-group">
                <label for="email">Email</label>
                <input 
                  type="email" 
                  id="email" 
                  v-model="formData.email" 
                  required
                  placeholder="Entrez votre email"
                  :class="{ 'input-error': fieldErrors.email }"
                />
                <div v-if="fieldErrors.email" class="error-message">{{ fieldErrors.email }}</div>
              </div>

              <div class="form-group">
                <label for="country">Pays</label>
                <select 
                  id="country" 
                  v-model="formData.country" 
                  required 
                  @change="updatePhonePrefix"
                  :class="{ 'input-error': fieldErrors.country }"
                >
                  <option value="" disabled selected>Sélectionnez votre pays</option>
                  <option v-for="country in countries" :key="country.code" :value="country.code">
                    {{ country.name }}
                  </option>
                </select>
                <div v-if="fieldErrors.country" class="error-message">{{ fieldErrors.country }}</div>
              </div>

              <div class="form-group">
                <label for="phone">Numéro de téléphone</label>
                <div 
                  class="phone-input-container"
                  :class="{ 'phone-input-error': fieldErrors.phoneNumber }"
                >
                  <span class="phone-prefix">{{ phonePrefix }}</span>
                  <input 
                    type="tel" 
                    id="phone" 
                    v-model="formData.phoneNumber" 
                    required
                    placeholder="Entrez votre numéro"
                    @input="validatePhoneNumber"
                  />
                </div>
                <small class="form-text phone-format">Format attendu: {{ getPhoneFormat() }}</small>
                <div v-if="fieldErrors.phoneNumber" class="error-message">{{ fieldErrors.phoneNumber }}</div>
              </div>

              <div v-if="fieldErrors.general" class="general-error-message">
                {{ fieldErrors.general }}
              </div>

              <div class="form-group">
                <label for="userType">Type d'inscription</label>
                <select id="userType" v-model="formData.userType" required>
                  <option value="personal">Je m'inscris pour moi-même</option>
                  <option value="provider">Je m'inscris en tant que tech provider pour mes clients</option>
                </select>
              </div>
            </div>
            
            <!-- Étape 2: Création du mot de passe -->
            <div v-if="currentStep === 2" class="step-content">
              <div class="form-group">
                <label for="password">Mot de passe</label>
                <input 
                  type="password" 
                  id="password" 
                  v-model="formData.password" 
                  required
                  placeholder="Choisissez un mot de passe"
                  :class="{ 'input-error': passwordError }"
                />
                <small class="form-text">Le mot de passe doit contenir au moins 8 caractères</small>
              </div>

              <div class="form-group">
                <label for="confirm-password">Confirmez le mot de passe</label>
                <input 
                  type="password" 
                  id="confirm-password" 
                  v-model="formData.confirmPassword" 
                  required
                  placeholder="Confirmez votre mot de passe"
                  :class="{ 'input-error': passwordError }"
                />
              </div>
              
              <div v-if="passwordError" class="error-message">
                {{ passwordError }}
              </div>

              <div v-if="fieldErrors.general" class="general-error-message">
                {{ fieldErrors.general }}
              </div>
            </div>
            
            <!-- Étape 3: Vérification humaine -->
            <div v-if="currentStep === 3" class="step-content">
              <div class="form-group captcha-group">
                <label>Vérification de sécurité</label>
                <p class="captcha-text">Pour confirmer que vous n'êtes pas un robot, veuillez compléter le captcha ci-dessous</p>
                
                <!-- Intégration d'un captcha plus réaliste -->
                <div class="captcha-wrapper">
                  <div class="real-captcha">
                    <div class="captcha-image">
                      <!-- Une image captcha simulée -->
                      <canvas ref="captchaCanvas" width="200" height="70"></canvas>
                      <button type="button" class="refresh-captcha" @click="generateCaptcha">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.3"/>
                        </svg>
                      </button>
                    </div>
                    <div class="captcha-input-wrapper">
                      <input 
                        type="text" 
                        v-model="captchaInput" 
                        placeholder="Entrez le texte de l'image" 
                        class="captcha-input"
                        @input="checkCaptcha"
                      />
                    </div>
                  </div>
                </div>
                
                <div class="mock-captcha" :class="{ 'captcha-verified': isHuman }">
                  <div class="captcha-checkbox" :class="{ 'checked': isHuman }"></div>
                  <span class="captcha-text">Je ne suis pas un robot</span>
                  <div v-if="isHuman" class="captcha-success">✓</div>
                </div>
                
                <div v-if="fieldErrors.general" class="general-error-message">
                  {{ fieldErrors.general }}
                </div>
              </div>

              <!-- Ajout de la case à cocher des conditions d'utilisation -->
              <div class="form-group terms-checkbox">
                <label class="checkbox-container">
                  <input 
                    type="checkbox" 
                    v-model="formData.termsAccepted"
                    :class="{ 'input-error': fieldErrors.terms }"
                  >
                  <span class="checkbox-text">
                    J'accepte les <a href="https://peelo.chat/cgu" target="_blank">conditions générales d'utilisation</a>
                  </span>
                </label>
                <div v-if="fieldErrors.terms" class="error-message">
                  {{ fieldErrors.terms }}
                </div>
              </div>
            </div>
            
            <!-- Message de succès après inscription -->
            <div v-if="registrationSuccess" class="success-animation">
              <div class="success-checkmark">
                <div class="check-icon">
                  <span class="icon-line line-tip"></span>
                  <span class="icon-line line-long"></span>
                  <div class="icon-circle"></div>
                  <div class="icon-fix"></div>
                </div>
              </div>
              <div class="success-message">
                <h3>Inscription réussie !</h3>
                <p>Vous allez être redirigé vers la page de connexion...</p>
              </div>
            </div>
            
            <!-- Navigation des étapes -->
            <div class="step-navigation">
              <button 
                v-if="currentStep > 1" 
                type="button" 
                class="back-btn" 
                @click="goToPreviousStep"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M19 12H5M12 19l-7-7 7-7"/>
                </svg>
              </button>
              
              <button 
                v-if="currentStep < totalSteps" 
                type="button" 
                class="next-btn" 
                @click="goToNextStep"
              >
                Suivant
              </button>
              
              <button 
                v-if="currentStep === totalSteps" 
                type="submit" 
                class="submit-btn full-width"
                :disabled="!isHuman || !formData.termsAccepted"
              >
                S'inscrire
              </button>
            </div>
          </form>

          <div class="separator">
            <span>ou</span>
          </div>

          <button class="connect-btn" @click="goToLogin">
            Se connecter
          </button>
          
          <p class="terms">
            En continuant, vous acceptez nos 
            <a href="https://peelo.chat/policy" target="_blank">Politique de confidentialité</a>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useRouter } from "vue-router";
import { ref, onMounted, onBeforeUnmount, nextTick, watch } from 'vue';
import axios from 'axios';
import { apiBaseUrl } from '@/config';

export default {
  name: 'UserRegister',
  setup() {
    const router = useRouter();
    const currentSlideIndex = ref(0);
    const currentSlide = ref(0);
    const isTransitioning = ref(false);
    const phonePrefix = ref("+221"); // Default to Senegal
    const currentStep = ref(1); // Pour suivre l'étape actuelle
    const totalSteps = 3; // Nombre total d'étapes
    const formData = ref({
      firstname: '',
      lastname: '',
      company: '',
      email: '',
      country: 'SN',
      phoneNumber: '',
      password: '',
      confirmPassword: '',
      userType: 'personal',
      termsAccepted: false // Ajout de la nouvelle propriété
    });
    const passwordError = ref('');
    const isHuman = ref(false);
    const captchaResponse = ref('');
    const captchaInput = ref('');
    const captchaText = ref('');
    const captchaCanvas = ref(null);
    const captchaGenerated = ref(false);
    const registrationSuccess = ref(false);
    
    // Variables pour les messages d'erreur spécifiques à chaque champ
    const fieldErrors = ref({
      firstname: '',
      lastname: '',
      company: '',
      email: '',
      phoneNumber: '',
      general: '' // Pour les erreurs générales
    });
    
    // Fonction pour réinitialiser les erreurs
    const resetFieldErrors = () => {
      Object.keys(fieldErrors.value).forEach(key => {
        fieldErrors.value[key] = '';
      });
    };
    
    const countries = ref([
      { code: 'AF', name: 'Afghanistan', prefix: '+93' },
      { code: 'ZA', name: 'Afrique du Sud', prefix: '+27' },
      { code: 'AL', name: 'Albanie', prefix: '+355' },
      { code: 'DZ', name: 'Algérie', prefix: '+213' },
      { code: 'DE', name: 'Allemagne', prefix: '+49' },
      { code: 'AD', name: 'Andorre', prefix: '+376' },
      { code: 'AO', name: 'Angola', prefix: '+244' },
      { code: 'AI', name: 'Anguilla', prefix: '+1264' },
      { code: 'AG', name: 'Antigua-et-Barbuda', prefix: '+1268' },
      { code: 'SA', name: 'Arabie saoudite', prefix: '+966' },
      { code: 'AR', name: 'Argentine', prefix: '+54' },
      { code: 'AM', name: 'Arménie', prefix: '+374' },
      { code: 'AW', name: 'Aruba', prefix: '+297' },
      { code: 'AU', name: 'Australie', prefix: '+61' },
      { code: 'AT', name: 'Autriche', prefix: '+43' },
      { code: 'AZ', name: 'Azerbaïdjan', prefix: '+994' },
      { code: 'BS', name: 'Bahamas', prefix: '+1242' },
      { code: 'BH', name: 'Bahreïn', prefix: '+973' },
      { code: 'BD', name: 'Bangladesh', prefix: '+880' },
      { code: 'BB', name: 'Barbade', prefix: '+1246' },
      { code: 'BE', name: 'Belgique', prefix: '+32' },
      { code: 'BZ', name: 'Belize', prefix: '+501' },
      { code: 'BJ', name: 'Bénin', prefix: '+229' },
      { code: 'BM', name: 'Bermudes', prefix: '+1441' },
      { code: 'BT', name: 'Bhoutan', prefix: '+975' },
      { code: 'BY', name: 'Biélorussie', prefix: '+375' },
      { code: 'BO', name: 'Bolivie', prefix: '+591' },
      { code: 'BA', name: 'Bosnie-Herzégovine', prefix: '+387' },
      { code: 'BW', name: 'Botswana', prefix: '+267' },
      { code: 'BR', name: 'Brésil', prefix: '+55' },
      { code: 'BN', name: 'Brunei', prefix: '+673' },
      { code: 'BG', name: 'Bulgarie', prefix: '+359' },
      { code: 'BF', name: 'Burkina Faso', prefix: '+226' },
      { code: 'BI', name: 'Burundi', prefix: '+257' },
      { code: 'KH', name: 'Cambodge', prefix: '+855' },
      { code: 'CM', name: 'Cameroun', prefix: '+237' },
      { code: 'CA', name: 'Canada', prefix: '+1' },
      { code: 'CV', name: 'Cap-Vert', prefix: '+238' },
      { code: 'CL', name: 'Chili', prefix: '+56' },
      { code: 'CN', name: 'Chine', prefix: '+86' },
      { code: 'CY', name: 'Chypre', prefix: '+357' },
      { code: 'CO', name: 'Colombie', prefix: '+57' },
      { code: 'KM', name: 'Comores', prefix: '+269' },
      { code: 'CG', name: 'Congo', prefix: '+242' },
      { code: 'CD', name: 'Congo (RDC)', prefix: '+243' },
      { code: 'KR', name: 'Corée du Sud', prefix: '+82' },
      { code: 'KP', name: 'Corée du Nord', prefix: '+850' },
      { code: 'CR', name: 'Costa Rica', prefix: '+506' },
      { code: 'CI', name: 'Côte d\'Ivoire', prefix: '+225' },
      { code: 'HR', name: 'Croatie', prefix: '+385' },
      { code: 'CU', name: 'Cuba', prefix: '+53' },
      { code: 'DK', name: 'Danemark', prefix: '+45' },
      { code: 'DJ', name: 'Djibouti', prefix: '+253' },
      { code: 'DM', name: 'Dominique', prefix: '+1767' },
      { code: 'EG', name: 'Égypte', prefix: '+20' },
      { code: 'AE', name: 'Émirats arabes unis', prefix: '+971' },
      { code: 'EC', name: 'Équateur', prefix: '+593' },
      { code: 'ER', name: 'Érythrée', prefix: '+291' },
      { code: 'ES', name: 'Espagne', prefix: '+34' },
      { code: 'EE', name: 'Estonie', prefix: '+372' },
      { code: 'US', name: 'États-Unis', prefix: '+1' },
      { code: 'ET', name: 'Éthiopie', prefix: '+251' },
      { code: 'FJ', name: 'Fidji', prefix: '+679' },
      { code: 'FI', name: 'Finlande', prefix: '+358' },
      { code: 'FR', name: 'France', prefix: '+33' },
      { code: 'GA', name: 'Gabon', prefix: '+241' },
      { code: 'GM', name: 'Gambie', prefix: '+220' },
      { code: 'GE', name: 'Géorgie', prefix: '+995' },
      { code: 'GH', name: 'Ghana', prefix: '+233' },
      { code: 'GI', name: 'Gibraltar', prefix: '+350' },
      { code: 'GR', name: 'Grèce', prefix: '+30' },
      { code: 'GD', name: 'Grenade', prefix: '+1473' },
      { code: 'GL', name: 'Groenland', prefix: '+299' },
      { code: 'GP', name: 'Guadeloupe', prefix: '+590' },
      { code: 'GU', name: 'Guam', prefix: '+1671' },
      { code: 'GT', name: 'Guatemala', prefix: '+502' },
      { code: 'GN', name: 'Guinée', prefix: '+224' },
      { code: 'GQ', name: 'Guinée équatoriale', prefix: '+240' },
      { code: 'GW', name: 'Guinée-Bissau', prefix: '+245' },
      { code: 'GY', name: 'Guyana', prefix: '+592' },
      { code: 'GF', name: 'Guyane française', prefix: '+594' },
      { code: 'HT', name: 'Haïti', prefix: '+509' },
      { code: 'HN', name: 'Honduras', prefix: '+504' },
      { code: 'HK', name: 'Hong Kong', prefix: '+852' },
      { code: 'HU', name: 'Hongrie', prefix: '+36' },
      { code: 'IN', name: 'Inde', prefix: '+91' },
      { code: 'ID', name: 'Indonésie', prefix: '+62' },
      { code: 'IQ', name: 'Irak', prefix: '+964' },
      { code: 'IR', name: 'Iran', prefix: '+98' },
      { code: 'IE', name: 'Irlande', prefix: '+353' },
      { code: 'IS', name: 'Islande', prefix: '+354' },
      { code: 'IL', name: 'Israël', prefix: '+972' },
      { code: 'IT', name: 'Italie', prefix: '+39' },
      { code: 'JM', name: 'Jamaïque', prefix: '+1876' },
      { code: 'JP', name: 'Japon', prefix: '+81' },
      { code: 'JO', name: 'Jordanie', prefix: '+962' },
      { code: 'KZ', name: 'Kazakhstan', prefix: '+7' },
      { code: 'KE', name: 'Kenya', prefix: '+254' },
      { code: 'KG', name: 'Kirghizistan', prefix: '+996' },
      { code: 'KI', name: 'Kiribati', prefix: '+686' },
      { code: 'KW', name: 'Koweït', prefix: '+965' },
      { code: 'LA', name: 'Laos', prefix: '+856' },
      { code: 'LS', name: 'Lesotho', prefix: '+266' },
      { code: 'LV', name: 'Lettonie', prefix: '+371' },
      { code: 'LB', name: 'Liban', prefix: '+961' },
      { code: 'LR', name: 'Libéria', prefix: '+231' },
      { code: 'LY', name: 'Libye', prefix: '+218' },
      { code: 'LI', name: 'Liechtenstein', prefix: '+423' },
      { code: 'LT', name: 'Lituanie', prefix: '+370' },
      { code: 'LU', name: 'Luxembourg', prefix: '+352' },
      { code: 'MO', name: 'Macao', prefix: '+853' },
      { code: 'MK', name: 'Macédoine du Nord', prefix: '+389' },
      { code: 'MG', name: 'Madagascar', prefix: '+261' },
      { code: 'MY', name: 'Malaisie', prefix: '+60' },
      { code: 'MW', name: 'Malawi', prefix: '+265' },
      { code: 'MV', name: 'Maldives', prefix: '+960' },
      { code: 'ML', name: 'Mali', prefix: '+223' },
      { code: 'MT', name: 'Malte', prefix: '+356' },
      { code: 'MA', name: 'Maroc', prefix: '+212' },
      { code: 'MQ', name: 'Martinique', prefix: '+596' },
      { code: 'MU', name: 'Maurice', prefix: '+230' },
      { code: 'MR', name: 'Mauritanie', prefix: '+222' },
      { code: 'YT', name: 'Mayotte', prefix: '+262' },
      { code: 'MX', name: 'Mexique', prefix: '+52' },
      { code: 'FM', name: 'Micronésie', prefix: '+691' },
      { code: 'MD', name: 'Moldavie', prefix: '+373' },
      { code: 'MC', name: 'Monaco', prefix: '+377' },
      { code: 'MN', name: 'Mongolie', prefix: '+976' },
      { code: 'ME', name: 'Monténégro', prefix: '+382' },
      { code: 'MS', name: 'Montserrat', prefix: '+1664' },
      { code: 'MZ', name: 'Mozambique', prefix: '+258' },
      { code: 'MM', name: 'Myanmar (Birmanie)', prefix: '+95' },
      { code: 'NA', name: 'Namibie', prefix: '+264' },
      { code: 'NR', name: 'Nauru', prefix: '+674' },
      { code: 'NP', name: 'Népal', prefix: '+977' },
      { code: 'NI', name: 'Nicaragua', prefix: '+505' },
      { code: 'NE', name: 'Niger', prefix: '+227' },
      { code: 'NG', name: 'Nigeria', prefix: '+234' },
      { code: 'NU', name: 'Niue', prefix: '+683' },
      { code: 'NO', name: 'Norvège', prefix: '+47' },
      { code: 'NC', name: 'Nouvelle-Calédonie', prefix: '+687' },
      { code: 'NZ', name: 'Nouvelle-Zélande', prefix: '+64' },
      { code: 'OM', name: 'Oman', prefix: '+968' },
      { code: 'UG', name: 'Ouganda', prefix: '+256' },
      { code: 'UZ', name: 'Ouzbékistan', prefix: '+998' },
      { code: 'PK', name: 'Pakistan', prefix: '+92' },
      { code: 'PW', name: 'Palaos', prefix: '+680' },
      { code: 'PS', name: 'Palestine', prefix: '+970' },
      { code: 'PA', name: 'Panama', prefix: '+507' },
      { code: 'PG', name: 'Papouasie-Nouvelle-Guinée', prefix: '+675' },
      { code: 'PY', name: 'Paraguay', prefix: '+595' },
      { code: 'NL', name: 'Pays-Bas', prefix: '+31' },
      { code: 'PE', name: 'Pérou', prefix: '+51' },
      { code: 'PH', name: 'Philippines', prefix: '+63' },
      { code: 'PL', name: 'Pologne', prefix: '+48' },
      { code: 'PF', name: 'Polynésie française', prefix: '+689' },
      { code: 'PT', name: 'Portugal', prefix: '+351' },
      { code: 'PR', name: 'Porto Rico', prefix: '+1787' },
      { code: 'QA', name: 'Qatar', prefix: '+974' },
      { code: 'RE', name: 'Réunion', prefix: '+262' },
      { code: 'RO', name: 'Roumanie', prefix: '+40' },
      { code: 'GB', name: 'Royaume-Uni', prefix: '+44' },
      { code: 'RU', name: 'Russie', prefix: '+7' },
      { code: 'RW', name: 'Rwanda', prefix: '+250' },
      { code: 'KN', name: 'Saint-Kitts-et-Nevis', prefix: '+1869' },
      { code: 'SM', name: 'Saint-Marin', prefix: '+378' },
      { code: 'VC', name: 'Saint-Vincent-et-les Grenadines', prefix: '+1784' },
      { code: 'LC', name: 'Sainte-Lucie', prefix: '+1758' },
      { code: 'SV', name: 'Salvador', prefix: '+503' },
      { code: 'WS', name: 'Samoa', prefix: '+685' },
      { code: 'ST', name: 'Sao Tomé-et-Principe', prefix: '+239' },
      { code: 'SN', name: 'Sénégal', prefix: '+221' },
      { code: 'RS', name: 'Serbie', prefix: '+381' },
      { code: 'SC', name: 'Seychelles', prefix: '+248' },
      { code: 'SL', name: 'Sierra Leone', prefix: '+232' },
      { code: 'SG', name: 'Singapour', prefix: '+65' },
      { code: 'SK', name: 'Slovaquie', prefix: '+421' },
      { code: 'SI', name: 'Slovénie', prefix: '+386' },
      { code: 'SO', name: 'Somalie', prefix: '+252' },
      { code: 'SD', name: 'Soudan', prefix: '+249' },
      { code: 'SS', name: 'Soudan du Sud', prefix: '+211' },
      { code: 'LK', name: 'Sri Lanka', prefix: '+94' },
      { code: 'SE', name: 'Suède', prefix: '+46' },
      { code: 'CH', name: 'Suisse', prefix: '+41' },
      { code: 'SR', name: 'Suriname', prefix: '+597' },
      { code: 'SZ', name: 'Eswatini', prefix: '+268' },
      { code: 'SY', name: 'Syrie', prefix: '+963' },
      { code: 'TJ', name: 'Tadjikistan', prefix: '+992' },
      { code: 'TW', name: 'Taïwan', prefix: '+886' },
      { code: 'TZ', name: 'Tanzanie', prefix: '+255' },
      { code: 'TD', name: 'Tchad', prefix: '+235' },
      { code: 'CZ', name: 'République tchèque', prefix: '+420' },
      { code: 'TH', name: 'Thaïlande', prefix: '+66' },
      { code: 'TL', name: 'Timor oriental', prefix: '+670' },
      { code: 'TG', name: 'Togo', prefix: '+228' },
      { code: 'TO', name: 'Tonga', prefix: '+676' },
      { code: 'TT', name: 'Trinité-et-Tobago', prefix: '+1868' },
      { code: 'TN', name: 'Tunisie', prefix: '+216' },
      { code: 'TM', name: 'Turkménistan', prefix: '+993' },
      { code: 'TR', name: 'Turquie', prefix: '+90' },
      { code: 'TV', name: 'Tuvalu', prefix: '+688' },
      { code: 'UA', name: 'Ukraine', prefix: '+380' },
      { code: 'UY', name: 'Uruguay', prefix: '+598' },
      { code: 'VU', name: 'Vanuatu', prefix: '+678' },
      { code: 'VA', name: 'Vatican', prefix: '+39' },
      { code: 'VE', name: 'Venezuela', prefix: '+58' },
      { code: 'VN', name: 'Viêt Nam', prefix: '+84' },
      { code: 'YE', name: 'Yémen', prefix: '+967' },
      { code: 'ZM', name: 'Zambie', prefix: '+260' },
      { code: 'ZW', name: 'Zimbabwe', prefix: '+263' }
    ]);
    
    const slides = ref([
      {
        title: "Un copilote pour vos ventes",
        description: "Réactif, intelligent, toujours prêt à conclure"
      },
      {
        title: "Répondez à vos clients",
        description: "Où que vous soyez, à tout moment"
      },
      {
        title: "Lancez votre business",
        description: "Créez votre chatbot sans appli mobile"
      }
    ]);

    let slideInterval;

    const setSlide = (index) => {
      if (isTransitioning.value) return;
      isTransitioning.value = true;
      currentSlideIndex.value = index;
      currentSlide.value = index;
      setTimeout(() => {
        isTransitioning.value = false;
      }, 800);
    };

    const startSlideShow = () => {
      slideInterval = setInterval(() => {
        setSlide((currentSlideIndex.value + 1) % slides.value.length);
      }, 7000);
    };

    const updatePhonePrefix = () => {
      const selectedCountry = countries.value.find(c => c.code === formData.value.country);
      if (selectedCountry) {
        phonePrefix.value = selectedCountry.prefix;
        // Vider le champ téléphone pour éviter les conflits de format
        formData.value.phoneNumber = '';
      }
    };

    // Référence des longueurs de numéros par pays
    const phoneLengths = {
      // Formats par pays: { min: nombre minimal de chiffres, max: nombre maximal de chiffres }
      FR: { min: 9, max: 10 },  // France: 9-10 chiffres
      SN: { min: 9, max: 9 },   // Sénégal: exactement 9 chiffres
      US: { min: 10, max: 10 }, // États-Unis: 10 chiffres
      CA: { min: 10, max: 10 }, // Canada: 10 chiffres
      GB: { min: 10, max: 11 }, // Royaume-Uni: 10-11 chiffres
      DE: { min: 10, max: 11 }, // Allemagne: 10-11 chiffres
      ES: { min: 9, max: 9 },   // Espagne: 9 chiffres
      IT: { min: 9, max: 10 },  // Italie: 9-10 chiffres
      MA: { min: 9, max: 9 },   // Maroc: 9 chiffres
      DZ: { min: 9, max: 9 },   // Algérie: 9 chiffres
      CI: { min: 8, max: 8 },   // Côte d'Ivoire: 8 chiffres
      CM: { min: 9, max: 9 },   // Cameroun: 9 chiffres
      MG: { min: 9, max: 9 },   // Madagascar: 9 chiffres
      TN: { min: 8, max: 8 }    // Tunisie: 8 chiffres
    };

    // Fonction par défaut pour les pays non listés
    const getDefaultPhoneLength = () => {
      return { min: 7, max: 15 }; // La plupart des numéros dans le monde ont entre 7 et 15 chiffres
    };

    // Fonction pour obtenir la longueur attendue selon le pays
    const getPhoneLength = (countryCode) => {
      return phoneLengths[countryCode] || getDefaultPhoneLength();
    };

    // Fonction pour valider et formater le numéro de téléphone
    const validatePhoneNumber = (event) => {
      // N'autoriser que les chiffres
      const value = event.target.value.replace(/\D/g, '');
      formData.value.phoneNumber = value;

      // Vérifier la longueur du numéro selon le pays
      const countryCode = formData.value.country;
      const { min, max } = getPhoneLength(countryCode);
      
      // Vérifier si le numéro a la bonne longueur
      if (value.length > 0) {
        if (value.length < min) {
          fieldErrors.value.phoneNumber = `Le numéro est trop court (${value.length}/${min} chiffres minimum)`;
        } else if (value.length > max) {
          fieldErrors.value.phoneNumber = `Le numéro est trop long (${value.length}/${max} chiffres maximum)`;
        } else {
          fieldErrors.value.phoneNumber = '';
        }
      } else {
        fieldErrors.value.phoneNumber = '';
      }
    };

    // Fonction pour obtenir le format attendu selon le pays
    const getPhoneFormat = () => {
      const selectedCountry = countries.value.find(c => c.code === formData.value.country);
      if (!selectedCountry) return '';

      const { min, max } = getPhoneLength(selectedCountry.code);
      let formatInfo = min === max ? `${min} chiffres` : `${min}-${max} chiffres`;
      
      switch (selectedCountry.code) {
        case 'FR':
          return `${formatInfo} - Ex: 6 12 34 56 78`; // Format français
        case 'SN':
          return `${formatInfo} - Ex: 77 123 45 67`;  // Format sénégalais
        case 'US':
        case 'CA':
          return `${formatInfo} - Ex: 123 456 7890`;  // Format nord-américain
        case 'GB':
          return `${formatInfo} - Ex: 7123 456789`;   // Format britannique
        case 'DE':
          return `${formatInfo} - Ex: 1512 3456789`;  // Format allemand
        case 'ES':
          return `${formatInfo} - Ex: 612 345 678`;   // Format espagnol
        case 'IT':
          return `${formatInfo} - Ex: 312 345 6789`;  // Format italien
        default:
          return `${formatInfo} - Ex: XX XX XX XX XX`; // Format générique
      }
    };

    // Navigation entre les étapes
    const goToNextStep = async () => {
      // Réinitialiser les erreurs précédentes
      resetFieldErrors();
      
      if (currentStep.value === 1) {
        // Valider les champs de la première étape
        let hasErrors = false;
        
        if (!formData.value.firstname) {
          fieldErrors.value.firstname = 'Le prénom est requis';
          hasErrors = true;
        }
        
        if (!formData.value.lastname) {
          fieldErrors.value.lastname = 'Le nom est requis';
          hasErrors = true;
        }
        
        if (!formData.value.company) {
          fieldErrors.value.company = "Le nom de l'entreprise est requis";
          hasErrors = true;
        }
        
        if (!formData.value.email) {
          fieldErrors.value.email = "L'email est requis";
          hasErrors = true;
        } else {
          // Validation email
          const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailPattern.test(formData.value.email)) {
            fieldErrors.value.email = "Veuillez entrer une adresse email valide";
            hasErrors = true;
          }
        }
        
        if (!formData.value.country) {
          fieldErrors.value.country = "Le pays est requis";
          hasErrors = true;
        }
        
        if (!formData.value.phoneNumber) {
          fieldErrors.value.phoneNumber = "Le numéro de téléphone est requis";
          hasErrors = true;
        } else {
          // Validation de la longueur du numéro de téléphone
          const { min, max } = getPhoneLength(formData.value.country);
          if (formData.value.phoneNumber.length < min) {
            fieldErrors.value.phoneNumber = `Le numéro est trop court (${formData.value.phoneNumber.length}/${min} chiffres minimum)`;
            hasErrors = true;
          } else if (formData.value.phoneNumber.length > max) {
            fieldErrors.value.phoneNumber = `Le numéro est trop long (${formData.value.phoneNumber.length}/${max} chiffres maximum)`;
            hasErrors = true;
          }
        }
        
        if (hasErrors) {
          return;
        }

        try {
          // Envoyer les données de la première phase au backend
          const phase1Response = await sendPhase1Data();
          if (!phase1Response.success) {
            // Traiter les erreurs spécifiques si elles sont disponibles
            if (phase1Response.field && phase1Response.message) {
              fieldErrors.value[phase1Response.field] = phase1Response.message;
            } else {
              fieldErrors.value.general = phase1Response.message || 'Erreur lors de la vérification des informations';
            }
            return;
          }
        } catch (error) {
          console.error('Erreur lors de l\'envoi des données de phase 1:', error);
          fieldErrors.value.general = 'Une erreur est survenue lors de la vérification de vos informations. Veuillez réessayer.';
          return;
        }
      }
      
      if (currentStep.value === 2) {
        // Valider les mots de passe
        if (!formData.value.password) {
          passwordError.value = 'Le mot de passe est requis';
          return;
        }
        
        if (formData.value.password.length < 8) {
          passwordError.value = 'Le mot de passe doit contenir au moins 8 caractères';
          return;
        }
        
        if (!formData.value.confirmPassword) {
          passwordError.value = 'Veuillez confirmer votre mot de passe';
          return;
        }
        
        if (formData.value.password !== formData.value.confirmPassword) {
          passwordError.value = 'Les mots de passe ne correspondent pas';
          return;
        }
        
        passwordError.value = '';
      }
      
      if (currentStep.value < totalSteps) {
        currentStep.value++;
        
        // Si nous passons à l'étape 3 (captcha), générer le captcha
        if (currentStep.value === 3) {
          nextTick(() => {
            // Essayer de générer le captcha plusieurs fois
            let attempts = 0;
            const maxAttempts = 5;
            const interval = setInterval(() => {
              if (captchaGenerated.value || attempts >= maxAttempts) {
                clearInterval(interval);
                return;
              }
              
              attempts++;
              
              generateCaptcha();
            }, 500);
          });
        }
      }
    };

    // Fonction pour envoyer les données de la première phase
    const sendPhase1Data = async () => {
      try {
        // Vérifier une dernière fois la longueur du numéro avant envoi
        const { min, max } = getPhoneLength(formData.value.country);
        if (formData.value.phoneNumber.length < min || formData.value.phoneNumber.length > max) {
          return { 
            success: false, 
            message: `Le numéro de téléphone doit contenir entre ${min} et ${max} chiffres.`,
            field: 'phoneNumber'
          };
        }
        
        // Format the phone number with the prefix
        const fullPhoneNumber = phonePrefix.value + formData.value.phoneNumber;
        
        const requestData = {
          firstname: formData.value.firstname,
          lastname: formData.value.lastname,
          company: formData.value.company,
          email: formData.value.email,
          country: formData.value.country,
          phoneNumber: fullPhoneNumber,
          userType: formData.value.userType,
          phase: 1 // Indiquer que c'est la phase 1
        };
        
        
        
        const response = await axios.post(`${apiBaseUrl}/strong/register/checking`, requestData);
        const data = response.data;
        
        // Stocker les informations de phase pour la requête finale
        localStorage.setItem("registrationPhase1", JSON.stringify({
          timestamp: Date.now(),
          validated: true
        }));

        return { success: true, data };
      } catch (error) {
        console.error('Erreur lors de l\'envoi des données de phase 1:', error);
        
        // Gestion spécifique des erreurs de validation du serveur
        if (error.response && error.response.status === 400) {
          const data = error.response.data;
          
          if (data.existingData) {
            // Vérifier si c'est l'email ou le téléphone qui est déjà utilisé
            const fullPhoneNumber = phonePrefix.value + formData.value.phoneNumber;
            const existingEmail = data.existingData.some(item => item.email === formData.value.email);
            const existingPhone = data.existingData.some(item => item.tel === fullPhoneNumber);
            
            if (existingEmail && existingPhone) {
              return { 
                success: false, 
                message: "L'email et le numéro de téléphone sont déjà utilisés.",
                field: 'general'
              };
            } else if (existingEmail) {
              return { 
                success: false, 
                message: "Cet email est déjà associé à un compte.", 
                field: 'email'
              };
            } else if (existingPhone) {
              return { 
                success: false, 
                message: "Ce numéro de téléphone est déjà associé à un compte.",
                field: 'phoneNumber'
              };
            }
          }
          
          return { 
            success: false, 
            message: data.error || 'Erreur lors de la vérification des informations',
            field: 'general'
          };
        }
        
        return { 
          success: false, 
          message: error.message || 'Erreur de connexion au serveur',
          field: 'general'
        };
      }
    };
    
    const goToPreviousStep = () => {
      if (currentStep.value > 1) {
        currentStep.value--;
      }
    };
    
    const verifyCaptcha = (response) => {
      captchaResponse.value = response;
      isHuman.value = !!response;
    };
    
    // Génération du captcha
    const generateCaptcha = () => {
      
      const canvas = captchaCanvas.value;
      if (!canvas) {
        
        return;
      }
      
      try {
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          
          return;
        }
        
        const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
        let captcha = '';
        
        // Générer 6 caractères aléatoires
        for (let i = 0; i < 6; i++) {
          captcha += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        
        captchaText.value = captcha;
        captchaInput.value = '';
        isHuman.value = false;
        
        // Dessiner le fond
        ctx.fillStyle = '#333';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // Ajouter du bruit
        for (let i = 0; i < 100; i++) {
          ctx.fillStyle = `rgba(${Math.random() * 255}, ${Math.random() * 255}, ${Math.random() * 255}, 0.5)`;
          ctx.fillRect(Math.random() * canvas.width, Math.random() * canvas.height, 2, 2);
        }
        
        // Dessiner des lignes aléatoires
        for (let i = 0; i < 5; i++) {
          ctx.strokeStyle = `rgba(${Math.random() * 255}, ${Math.random() * 255}, ${Math.random() * 255}, 0.5)`;
          ctx.beginPath();
          ctx.moveTo(Math.random() * canvas.width, Math.random() * canvas.height);
          ctx.lineTo(Math.random() * canvas.width, Math.random() * canvas.height);
          ctx.stroke();
        }
        
        // Dessiner le texte
        ctx.font = 'bold 24px Arial';
        ctx.fillStyle = 'white';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        
        // Ajuster l'espacement
        const textWidth = ctx.measureText(captcha).width;
        const spacing = (canvas.width - textWidth) / (captcha.length + 1);
        
        for (let i = 0; i < captcha.length; i++) {
          const x = (i + 1) * spacing + textWidth * (i / captcha.length);
          const y = canvas.height / 2 + Math.random() * 8 - 4;
          const rotation = (Math.random() - 0.5) * 0.4;
          
          ctx.save();
          ctx.translate(x, y);
          ctx.rotate(rotation);
          ctx.fillText(captcha[i], 0, 0);
          ctx.restore();
        }
        
        captchaGenerated.value = true;
        
      } catch (error) {
        console.error("Error generating captcha:", error);
      }
    };
    
    const checkCaptcha = () => {
      if (captchaInput.value.toLowerCase() === captchaText.value.toLowerCase()) {
        isHuman.value = true;
        captchaResponse.value = 'valid-captcha-response';  // Valeur spécifique attendue par le serveur
      } else {
        isHuman.value = false;
        captchaResponse.value = '';
      }
    };
    
    const goToLogin = () => {
      router.push('/login');
    };
    
    const registerUser = async () => {
      try {
        // Réinitialiser les erreurs précédentes
        resetFieldErrors();
        
        // Vérifier que les conditions d'utilisation sont acceptées
        if (!formData.value.termsAccepted) {
          fieldErrors.value.terms = "Vous devez accepter les conditions d'utilisation pour continuer";
          return;
        }
        
        // Vérifier que la captcha est validée
        if (!isHuman.value) {
          fieldErrors.value.general = 'Veuillez confirmer que vous n\'êtes pas un robot';
          return;
        }
        
        // Vérifier que le captcha a bien été résolu
        if (!captchaResponse.value || captchaResponse.value !== 'valid-captcha-response') {
          fieldErrors.value.general = 'Veuillez résoudre correctement le captcha';
          return;
        }
        
        // Vérifier que la phase 1 a bien été validée
        const phase1Data = localStorage.getItem("registrationPhase1");
        if (!phase1Data) {
          fieldErrors.value.general = 'Erreur dans le processus d\'inscription. Veuillez recommencer.';
          currentStep.value = 1;
          return;
        }
        
        const phase1Info = JSON.parse(phase1Data);
        // Vérifier si la validation n'est pas trop ancienne (30 minutes max)
        const thirtyMinutesAgo = Date.now() - 30 * 60 * 1000;
        if (!phase1Info.validated || phase1Info.timestamp < thirtyMinutesAgo) {
          fieldErrors.value.general = 'Votre session d\'inscription a expiré. Veuillez recommencer.';
          currentStep.value = 1;
          return;
        }
        
        // Vérifier une dernière fois la longueur du numéro avant envoi
        const { min, max } = getPhoneLength(formData.value.country);
        if (formData.value.phoneNumber.length < min || formData.value.phoneNumber.length > max) {
          fieldErrors.value.general = `Le numéro de téléphone doit contenir entre ${min} et ${max} chiffres.`;
          currentStep.value = 1; // Revenir à l'étape des informations personnelles
          return;
        }
        
        // Format the phone number with the prefix
        const fullPhoneNumber = phonePrefix.value + formData.value.phoneNumber;
        
        const requestData = {
          firstname: formData.value.firstname,
          lastname: formData.value.lastname,
          company: formData.value.company,
          email: formData.value.email,
          country: formData.value.country,
          phoneNumber: fullPhoneNumber,
          userType: formData.value.userType,
          password: formData.value.password,
          captchaResponse: captchaResponse.value,
          phase: 2 // Indiquer que c'est la phase finale
        };
        
        // Supprime les champs inutiles pour l'API
        delete requestData.confirmPassword;
        
        
        
        try {
          const response = await axios.post(`${apiBaseUrl}/strong/register/final`, requestData);
          const data = response.data;
  
          // Supprimer les données temporaires de phase 1
          localStorage.removeItem("registrationPhase1");
          
          
          
          // Créer un objet utilisateur formaté
          const userData = {
            id: data.data.id,
            name: data.data.name,
            email: data.data.email,
            tel: data.data.tel,
            profile_picture: data.data.profile_picture,
            token: data.data.id, // Utiliser l'ID comme token temporaire si nécessaire
            provenance: data.data.provenance || 'register'
          };
          
          // Stocker les informations de l'utilisateur
         // localStorage.setItem("user", JSON.stringify({ data: userData }));
          
          // Afficher le message de succès et attendre avant la redirection
          registrationSuccess.value = true;
          
          // Rediriger vers le tableau de bord après un délai pour montrer l'animation
          setTimeout(async () => {
            await router.push("/login");
          }, 2000);
        } catch (error) {
          console.error("Erreur lors de l'inscription:", error);
          
          // Gestion spécifique de l'erreur de captcha
          if (error.response && error.response.status === 400) {
            const errorData = error.response.data;
            
            if (errorData.captcha) {
              // Le captcha est invalide, on recharge le captcha
              generateCaptcha();
              captchaInput.value = '';
              isHuman.value = false;
              fieldErrors.value.general = 'Captcha invalide. Veuillez réessayer.';
              return;
            }
          }
          
          // Autres erreurs
          if (error.response && error.response.data) {
            fieldErrors.value.general = error.response.data.error || "Échec de l'inscription: " + error.message;
          } else {
            fieldErrors.value.general = "Échec de l'inscription: " + error.message;
          }
        }
      } catch (error) {
        console.error("Erreur inattendue:", error);
        fieldErrors.value.general = "Une erreur inattendue s'est produite. Veuillez réessayer.";
      }
    };

    onMounted(() => {
      startSlideShow();
      updatePhonePrefix();
      
      // Initialiser le captcha directement si on est à l'étape 3
      if (currentStep.value === 3) {
        initCaptcha();
      }
      
      // Surveiller les changements d'étape pour générer le captcha quand nécessaire
      watch(() => currentStep.value, (newStep) => {
        if (newStep === 3 && !captchaGenerated.value) {
          initCaptcha();
        }
      });
    });

    // Fonction d'initialisation du captcha avec plusieurs tentatives
    const initCaptcha = () => {
      nextTick(() => {
        // Essayer de générer le captcha plusieurs fois
        let attempts = 0;
        const maxAttempts = 5;
        const interval = setInterval(() => {
          if (captchaGenerated.value || attempts >= maxAttempts) {
            clearInterval(interval);
            return;
          }
          
          attempts++;
          
          generateCaptcha();
        }, 500);
      });
    };

    onBeforeUnmount(() => {
      clearInterval(slideInterval);
    });

    return {
      router,
      currentSlideIndex,
      currentSlide,
      slides,
      formData,
      countries,
      phonePrefix,
      currentStep,
      totalSteps,
      passwordError,
      isHuman,
      captchaInput,
      captchaCanvas,
      setSlide,
      registerUser,
      updatePhonePrefix,
      goToNextStep,
      goToPreviousStep,
      generateCaptcha,
      checkCaptcha,
      apiBaseUrl,
      goToLogin,
      fieldErrors,
      resetFieldErrors,
      validatePhoneNumber,
      getPhoneFormat,
      getPhoneLength,
      registrationSuccess
    };
  },
  mounted() {
    // Pas besoin d'initier le SDK Facebook
  },
  methods: {
    // Suppression des méthodes Facebook non utilisées
  }
};
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  display: grid;
  grid-template-columns: 1fr 1fr;
  background-color: #1a1a1a;
  overflow: hidden;
}

.left-section {
  position: relative;
  overflow: hidden;
  border-radius: 24px;
  margin: 1rem;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 2rem);
  max-height: calc(100vh - 2rem);
}

.left-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('@/assets/peelochat_ecommerce.png');
  background-size: cover;
  background-position: center;
  transition: transform 0.8s ease;
  z-index: 0;
}

.left-section:hover::before {
  transform: scale(1.1);
}

.left-section::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to bottom, rgba(37, 211, 102, 0.6), rgba(37, 211, 102, 0.2));
  z-index: 1;
}

.left-section > * {
  position: relative;
  z-index: 2;
}

.logo {
  position: relative;
  margin-bottom: 2rem;
}

.logo img {
  height: 40px;
  width: auto;
}

.back-link {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: white;
  text-decoration: none;
  margin-bottom: 2rem;
  width: fit-content;
}

.hero-content {
  margin-top: auto;
  margin-bottom: 3rem;
  color: white;
  position: relative;
  height: auto;
  min-height: 220px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  overflow: visible;
}

.slide {
  padding: 0 3rem;
  position: absolute;
  width: 100%;
  overflow: visible;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

.slide h2 {
  font-size: 2.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.slide p {
  font-size: 1.2rem;
  line-height: 1.5;
  opacity: 0.9;
  margin-bottom: 2rem;
}

.dots {
  display: flex;
  gap: 0.5rem;
  padding: 0 3rem;
  margin-top: 6rem;
  justify-content: center;
}

.dot {
  width: 1rem;
  height: 0.5rem;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.dot:hover {
  background: rgba(255, 255, 255, 0.4);
}

.dot.active {
  background: white;
  width: 4rem;
}

.right-section {
  padding: 2rem 4rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100vh;
  overflow: hidden;
}

.auth-container {
  max-width: 400px;
  width: 100%;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.form-container {
  flex: 1;
  overflow-y: auto;
  padding-right: 20px;
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: thin;  /* Firefox */
  padding-right: 50px;
}

.form-container::-webkit-scrollbar {
  width: 4px;
}

.form-container::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
}

.form-container::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
}

h1 {
  color: white;
  font-size: 2.5rem;
  font-weight: 500;
  margin-bottom: 1rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.login-text {
  color: #8f8f8f;
  margin-bottom: 2rem;
}

.login-link {
  color: white;
  text-decoration: none;
  font-weight: 500;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  color: white;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
}

.form-group input,
.form-group select {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  background: transparent;
  color: white;
  font-size: 1rem;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: rgba(255, 255, 255, 0.3);
}

.form-group select {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-position: calc(100% - 12px) 50%;
  background-repeat: no-repeat;
  background-size: 16px;
  padding-right: 2.5rem;
}

.submit-btn {
  width: 100%;
  padding: 0.75rem;
  border: none;
  border-radius: 8px;
  background: white;
  color: #1a1a1a;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 1rem;
}

.submit-btn:hover {
  background: rgba(255, 255, 255, 0.9);
  transform: translateY(-1px);
}

.submit-btn:active {
  transform: translateY(0);
}

.separator {
  display: flex;
  align-items: center;
  text-align: center;
  margin: 2rem 0;
  color: #8f8f8f;
}

.separator::before,
.separator::after {
  content: '';
  flex: 1;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.separator span {
  padding: 0 10px;
}

.connect-btn {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  background: transparent;
  color: white;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.connect-btn:hover {
  background: rgba(255, 255, 255, 0.05);
  transform: translateY(-1px);
}

.connect-btn:active {
  transform: translateY(0);
}

.terms {
  margin-top: 2rem;
  font-size: 0.875rem;
  color: #8f8f8f;
  text-align: center;
}

.terms a {
  color: white;
  text-decoration: none;
  font-weight: 500;
}

.terms a:hover {
  text-decoration: underline;
}

.phone-input-container {
  display: flex;
  align-items: center;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  overflow: hidden;
}

.phone-prefix {
  background: rgba(255, 255, 255, 0.05);
  color: white;
  padding: 0.75rem;
  min-width: 60px;
  text-align: center;
  border-right: 1px solid rgba(255, 255, 255, 0.1);
}

.phone-input-container input {
  border: none;
  flex: 1;
}

.phone-input-container input:focus {
  outline: none;
}

/* Enhanced transitions */
.fade-slide-enter-active {
  transition: all 0.8s ease-out;
  transition-delay: 0.2s;
}

.fade-slide-leave-active {
  transition: all 0.6s ease-in;
  position: absolute;
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(30px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-30px);
}

@media (max-width: 768px) {
  .login-container {
    grid-template-columns: 1fr;
    overflow-y: auto;
  }

  .right-section {
    padding: 2rem;
    min-height: 100vh;
  }

  .auth-container {
    margin: 0 auto;
    padding: 0 1rem;
  }

  h1 {
    font-size: 2rem;
  }

  .slide h2 {
    font-size: 2rem;
  }
}

/* Styles pour les étapes */
.steps-progress {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 2rem;
  position: relative;
}

.step-indicator {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 15px;
  position: relative;
  z-index: 2;
  transition: all 0.3s ease;
}

.step-indicator.active {
  background-color: white;
  color: #1a1a1a;
  transform: scale(1.1);
}

.step-indicator.completed {
  background-color: #4CAF50;
  color: white;
}

.progress-line {
  position: absolute;
  height: 2px;
  background-color: rgba(255, 255, 255, 0.1);
  width: 70%;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
}

.step-content {
  animation: fadeIn 0.5s ease;
}

.step-navigation {
  display: flex;
  justify-content: space-between;
  margin-top: 2rem;
  gap: 1rem;
}

.back-btn, .next-btn {
  padding: 0.75rem 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  background: transparent;
  color: white;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
}

.back-btn {
  background-color: transparent;
  padding: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.next-btn {
  background-color: rgba(255, 255, 255, 0.1);
}

.submit-btn.full-width {
  width: 100%;
  margin-left: auto;
}

.form-text {
  color: #8f8f8f;
  font-size: 0.75rem;
  margin-top: 0.5rem;
}

.phone-format {
  color: rgba(255, 255, 255, 0.6);
  font-style: italic;
  margin-bottom: 0.5rem;
}

.error-message {
  color: #ff5252;
  font-size: 0.875rem;
  margin-top: 0.5rem;
  animation: fadeIn 0.3s ease;
}

.general-error-message {
  color: #ff5252;
  font-size: 0.875rem;
  margin-top: 1rem;
  padding: 0.75rem;
  background-color: rgba(255, 82, 82, 0.1);
  border-radius: 8px;
  border-left: 3px solid #ff5252;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Styles pour le captcha */
.captcha-group {
  margin-top: 2rem;
}

.captcha-text {
  color: #8f8f8f;
  font-size: 0.875rem;
  margin-bottom: 1rem;
}

.captcha-wrapper {
  display: flex;
  justify-content: center;
}

.real-captcha {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  background-color: rgba(255, 255, 255, 0.05);
}

.captcha-image {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.captcha-image canvas {
  width: 100px;
  height: 35px;
}

.captcha-image button {
  background: none;
  border: none;
  cursor: pointer;
}

.captcha-input-wrapper {
  flex: 1;
  margin-left: 0.75rem;
}

.captcha-input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  background: transparent;
  color: white;
}

.captcha-input:focus {
  outline: none;
  border-color: rgba(255, 255, 255, 0.3);
}

.mock-captcha {
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  padding: 1rem;
  margin-top: 1rem;
  display: flex;
  align-items: center;
  cursor: pointer;
  background-color: rgba(255, 255, 255, 0.05);
  transition: all 0.3s ease;
}

.mock-captcha:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.captcha-checkbox {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.5);
  border-radius: 2px;
  margin-right: 0.75rem;
  position: relative;
}

.captcha-verified {
  background-color: rgba(76, 175, 80, 0.1);
  border-color: #4CAF50;
}

.captcha-success {
  color: #4CAF50;
  font-size: 1.2rem;
  margin-left: 0.75rem;
}

.captcha-checkbox.checked {
  background-color: #4CAF50;
  border-color: #4CAF50;
}

.captcha-checkbox.checked::after {
  content: '✓';
  position: absolute;
  color: white;
  font-size: 14px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.refresh-captcha {
  color: rgba(255, 255, 255, 0.7);
  transition: color 0.2s;
}

.refresh-captcha:hover {
  color: white;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.back-btn:hover, .next-btn:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateY(-1px);
}

.submit-btn:disabled {
  background: rgba(255, 255, 255, 0.5);
  cursor: not-allowed;
}

.input-error {
  border-color: #ff5252 !important;
}

.phone-input-error {
  border-color: #ff5252 !important;
}

/* Styles pour le message de succès après inscription */
.success-animation {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  z-index: 999;
  animation: fadeIn 0.3s ease;
}

.success-checkmark {
  width: 80px;
  height: 80px;
  margin: 0 auto;
  margin-bottom: 20px;
  position: relative;
}

.check-icon {
  width: 80px;
  height: 80px;
  position: relative;
  border-radius: 50%;
  box-sizing: content-box;
  border: 4px solid #4CAF50;
}

.check-icon::before {
  top: 3px;
  left: -2px;
  width: 30px;
  transform-origin: 100% 50%;
  border-radius: 100px 0 0 100px;
}

.check-icon::after {
  top: 0;
  left: 30px;
  width: 60px;
  transform-origin: 0 50%;
  border-radius: 0 100px 100px 0;
  animation: rotate-circle 4.25s ease-in;
}

.check-icon::before, .check-icon::after {
  content: '';
  height: 100px;
  position: absolute;
  background: rgba(0, 0, 0, 0.8);
  transform: rotate(-45deg);
}

.icon-line {
  height: 5px;
  background-color: #4CAF50;
  display: block;
  border-radius: 2px;
  position: absolute;
  z-index: 10;
}

.icon-line.line-tip {
  top: 46px;
  left: 14px;
  width: 25px;
  transform: rotate(45deg);
  animation: icon-line-tip 0.75s;
}

.icon-line.line-long {
  top: 38px;
  right: 8px;
  width: 47px;
  transform: rotate(-45deg);
  animation: icon-line-long 0.75s;
}

.icon-circle {
  top: -4px;
  left: -4px;
  z-index: 10;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  position: absolute;
  box-sizing: content-box;
  border: 4px solid rgba(76, 175, 80, 0.5);
}

.icon-fix {
  top: 8px;
  width: 5px;
  left: 26px;
  z-index: 1;
  height: 85px;
  position: absolute;
  transform: rotate(-45deg);
  background-color: rgba(0, 0, 0, 0.8);
}

.success-message {
  color: white;
  text-align: center;
  animation: fadeInUp 0.5s ease 0.5s both;
}

.success-message h3 {
  font-size: 1.8rem;
  margin-bottom: 10px;
  color: #4CAF50;
}

.success-message p {
  font-size: 1.2rem;
  opacity: 0.9;
}

@keyframes icon-line-tip {
  0% {
    width: 0;
    left: 1px;
    top: 19px;
  }
  54% {
    width: 0;
    left: 1px;
    top: 19px;
  }
  70% {
    width: 50px;
    left: -8px;
    top: 37px;
  }
  84% {
    width: 17px;
    left: 21px;
    top: 48px;
  }
  100% {
    width: 25px;
    left: 14px;
    top: 45px;
  }
}

@keyframes icon-line-long {
  0% {
    width: 0;
    right: 46px;
    top: 54px;
  }
  65% {
    width: 0;
    right: 46px;
    top: 54px;
  }
  84% {
    width: 55px;
    right: 0px;
    top: 35px;
  }
  100% {
    width: 47px;
    right: 8px;
    top: 38px;
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Styles pour la case à cocher des conditions d'utilisation */
.terms-checkbox {
  margin-top: 2rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
}

.terms-checkbox:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.2);
}

.checkbox-container {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  position: relative;
  padding-left: 35px;
}

.checkbox-container input[type="checkbox"] {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

/* Style personnalisé pour la case à cocher */
.checkbox-container:before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 22px;
  height: 22px;
  background: transparent;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  transition: all 0.2s ease;
}

/* Style quand la case est cochée */
.checkbox-container input:checked ~ .checkbox-text:before {
  content: '✓';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 22px;
  height: 22px;
  background: #25D366;
  border-radius: 4px;
  border: 2px solid #25D366;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}

/* Style au survol */
.checkbox-container:hover:before {
  border-color: rgba(255, 255, 255, 0.5);
}

.checkbox-text {
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  line-height: 1.4;
}

.checkbox-text a {
  color: #25D366;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.2s ease;
}

.checkbox-text a:hover {
  color: #2EE375;
  text-decoration: underline;
}

/* Style d'erreur pour la case à cocher */
.checkbox-container.input-error:before {
  border-color: #ff5252;
}

.submit-btn:disabled {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.5);
  cursor: not-allowed;
  transform: none;
}

.submit-btn:disabled:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: none;
}

.error-message {
  color: #ff5252;
  font-size: 0.875rem;
  margin-top: 0.75rem;
  padding-left: 35px;
}

.back-button-container {
  margin-bottom: 1.5rem;
}

.back-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  font-size: 0.95rem;
  transition: all 0.2s ease;
  padding: 0.5rem 0;
}

.back-button:hover {
  color: white;
  transform: translateX(-5px);
}

.back-button svg {
  transition: transform 0.2s ease;
}

.back-button:hover svg {
  transform: translateX(-3px);
}
</style> 